import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const Payouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchPayouts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations/my-payouts?page=${pagination.page}&limit=${pagination.limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPayouts(response.data.data.payouts || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        totalPages: response.data.data.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to load payouts:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchPayouts();
  }, [pagination.page, fetchPayouts]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'UGX',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <h1 className="h3 mb-1" style={{ fontWeight: '600' }}>Payout History</h1>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          View all payouts received from the platform
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          {payouts.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-wallet2" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="text-muted mt-3 mb-0">No payouts yet</p>
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                Payouts will appear here once the platform admin processes them
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-end">Amount</th>
                      <th className="text-end">Fee</th>
                      <th className="text-end">Net Received</th>
                      <th>Payment Method</th>
                      <th>Reference</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id}>
                        <td>
                          <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                            {new Date(payout.processedAt || payout.createdAt).toLocaleDateString()}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {new Date(payout.processedAt || payout.createdAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="text-end">{formatAmount(payout.amount)}</td>
                        <td className="text-end">
                          <span className="text-danger">{formatAmount(payout.fee)}</span>
                        </td>
                        <td className="text-end" style={{ fontWeight: '600', color: '#10b981' }}>
                          {formatAmount(payout.netAmount)}
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {payout.paymentMethod || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <code style={{ fontSize: '0.75rem' }}>
                            {payout.reference || '-'}
                          </code>
                        </td>
                        <td>
                          <span className={`badge ${
                            payout.status === 'completed' ? 'bg-success' :
                            payout.status === 'failed' ? 'bg-danger' :
                            payout.status === 'processing' ? 'bg-info' :
                            'bg-warning'
                          }`}>
                            {payout.status}
                          </span>
                        </td>
                        <td>
                          {payout.notes ? (
                            <div style={{ fontSize: '0.75rem', maxWidth: '200px' }}>
                              {payout.notes}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </div>
                  <div className="btn-group">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payouts;
